import React, { useMemo } from "react";
import Highcharts from "../../lib/highchartsExtended";
import type { Options as HCOptions } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../providers/ThemeProvider";
import type { StopCause } from "../../services/ShiftAnalysisService";
import { formatDuration, buildMachineColorMap } from "./utils";
import Panel from "./Panel";

interface Props {
  causes: StopCause[];
  focusedSn?: string | null;
  onHover?: (sn: string | null) => void;
  onPin?: (sn: string) => void;
}

const DowntimeSankey: React.FC<Props> = ({
  causes,
  focusedSn,
  onHover,
  onPin,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark;

  const textColor = isDark ? "#cbd5e1" : "#475569";

  const options = useMemo<HCOptions>(() => {
    if (causes.length === 0)
      return { chart: { backgroundColor: "transparent" } };

    // Build machine color map from unique machines in causes.
    const uniqueMachines = Array.from(
      new Map(
        causes.map((c) => [c.machine_sn, { machine_sn: c.machine_sn }]),
      ).values(),
    );
    const colorMap = buildMachineColorMap(uniqueMachines);

    // Left nodes = machines, right nodes = faults, links weighted by total_seconds.
    // Take top causes to keep it readable (max 12 links).
    const top = causes.slice(0, 12);

    // Unique faults (right side). Prefix with a unique key to avoid collision
    // with machine names that might match a fault name.
    const faultKey = (text: string) => `fault:${text}`;

    const links = top.map((c) => ({
      from: c.machine_name,
      to: faultKey(c.fault_text),
      weight: c.total_seconds,
      color:
        focusedSn != null && focusedSn !== c.machine_sn
          ? isDark
            ? "rgba(148,163,184,0.15)"
            : "rgba(15,23,42,0.08)"
          : (colorMap.get(c.machine_sn) ?? "#6366f1") + "99",
      custom: { sn: c.machine_sn },
    }));

    // Node definitions for coloring machines.
    const machineNodes = Array.from(new Set(top.map((c) => c.machine_sn))).map(
      (sn) => {
        const name = top.find((c) => c.machine_sn === sn)!.machine_name;
        return {
          id: name,
          color: colorMap.get(sn) ?? "#6366f1",
        };
      },
    );

    return {
      chart: {
        backgroundColor: "transparent",
        height: 320,
        spacing: [10, 10, 10, 10],
        animation: { duration: 900 },
      },
      title: undefined,
      credits: { enabled: false },
      tooltip: {
        useHTML: true,
        backgroundColor: isDark ? "#1e293b" : "#ffffff",
        borderRadius: 10,
        style: { color: isDark ? "#f1f5f9" : "#0f172a", fontSize: "12px" },
        nodeFormatter() {
          const node = this as unknown as { name: string; sum: number };
          const label = node.name.startsWith("fault:")
            ? node.name.slice(6)
            : node.name;
          return `<b>${label}</b><br/>${formatDuration(node.sum)}`;
        },
        pointFormatter() {
          const p = this as unknown as {
            fromNode: { name: string };
            toNode: { name: string };
            weight: number;
          };
          const fault = p.toNode.name.startsWith("fault:")
            ? p.toNode.name.slice(6)
            : p.toNode.name;
          return `<b>${p.fromNode.name}</b> → <b>${fault}</b><br/>${formatDuration(p.weight)}`;
        },
      },
      plotOptions: {
        sankey: {
          cursor: "pointer",
          nodeWidth: 20,
          nodePadding: 14,
          borderRadius: 4,
          dataLabels: {
            enabled: true,
            style: {
              color: textColor,
              fontSize: "11px",
              fontWeight: "500",
              textOutline: "none",
            },
            nodeFormatter() {
              const node = this as unknown as { point: { name: string } };
              const label = node.point.name.startsWith("fault:")
                ? node.point.name.slice(6)
                : node.point.name;
              return label.length > 22 ? `${label.slice(0, 20)}…` : label;
            },
          },
          point: {
            events: {
              mouseOver() {
                const p = this as unknown as { custom?: { sn?: string } };
                if (p.custom?.sn) onHover?.(p.custom.sn);
              },
              mouseOut() {
                onHover?.(null);
              },
              click() {
                const p = this as unknown as { custom?: { sn?: string } };
                if (p.custom?.sn) onPin?.(p.custom.sn);
              },
            },
          },
        },
      },
      series: [
        {
          type: "sankey",
          name: t("downtimeFlow", "Downtime flow"),
          keys: ["from", "to", "weight", "color", "custom"],
          data: links.map((l) => [l.from, l.to, l.weight, l.color, l.custom]),
          nodes: machineNodes,
        } as any,
      ],
    };
  }, [causes, focusedSn, isDark, textColor, onHover, onPin, t]);

  if (causes.length === 0) return null;

  return (
    <Panel
      label={t("whereDidTheTimeGo", "Where did the time go?")}
      meta={t("machineToFaultFlow", "machine → fault → downtime")}
    >
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Panel>
  );
};

export default DowntimeSankey;
